import {WorkingDay} from '@domain/working-days';
import {ServiceLocation} from '../enum/location.enum';
import {OnlineIntegrationType} from '../enum/online-integration-type.enum';

export interface ClinicWithId extends Clinic
{
    id: string;
}

interface Contact
{
    code: string;
    number: number;
}

interface Specialty
{
    name: string;
    clinicians: {
        name?: string;
        email: string;
        picture?: string;
    }[];
}

type CancelationTaxType = 'none' | 'fixed' | 'variable';

interface CancelationAmount
{
    daysBefore: number;
    amount: number;
}

interface CancelationTax
{
    cancelationTaxType: CancelationTaxType;
    amount: CancelationAmount;
}

export interface OnlineIntegration
{
    integrationType?: OnlineIntegrationType;
    apiEndpoint?: string;
    user?: string;
    password?: string;
    token?: string;
    externalBookingLink?: string;
}

export interface Configurations
{
    appointmentsScheduledNotification: boolean;
    appointmentsCancelationNotification: boolean;
    cancelationTax: CancelationTax;
    onlineIntegration?: OnlineIntegration;
}

export class Clinic
{
    public readonly name: string;

    public readonly email: string;

    public readonly address: string;

    public readonly zipCode: string;

    public readonly iban: string;

    public readonly contact: Contact;

    public readonly workingDays: WorkingDay[];

    public readonly specialties: Specialty[];

    public readonly manager: string;

    public readonly picture?: string;
    
    public readonly serviceLocationTypes?: ServiceLocation[];

    public readonly configurations?: Configurations;
    
    public readonly onlineIntegration?: OnlineIntegration;
    
    public readonly location?: string;

    public constructor(
        name: string,
        email: string,
        address: string,
        zipCode: string,
        iban: string,
        contact: Contact,
        workingDays: WorkingDay[],
        manager: string,
        specialties: Specialty[],
        picture?: string,
        serviceLocationTypes?: ServiceLocation[],
        configurations?: Configurations,
        onlineIntegration?: OnlineIntegration,
        location?: string
    )
    {
        this.name = name;
        this.email = email;
        this.address = address;
        this.zipCode = zipCode;
        this.iban = iban;
        this.contact = contact;
        this.workingDays = workingDays;
        this.specialties = specialties;
        this.manager = manager;
        this.picture = picture;
        this.serviceLocationTypes = serviceLocationTypes;
        this.configurations = configurations;
        this.onlineIntegration = onlineIntegration;
        this.location = location;
    }

    public static fromJSON(clinicJSON: any): Clinic
    {
        return new Clinic(
            clinicJSON.name,
            clinicJSON.email,
            clinicJSON.address,
            clinicJSON.zipCode,
            clinicJSON.iban,
            clinicJSON.contact,
            clinicJSON.workingDays,
            clinicJSON?.manager ?? '-',
            clinicJSON.specialities?.length > 0 ? clinicJSON.specialities.map((specialty: Specialty) => ({
                ...specialty,
                clinicians: specialty.clinicians?.length > 0 ? specialty.clinicians.map((clinician: {name?: string; email: string; picture?: string}) => ({
                    ...clinician,
                    picture: clinician?.picture ?? 'assets/images/avatar_placeholder.webp'
                })) : []
            })) : [],
            clinicJSON?.picture ?? 'assets/images/clinic_placeholder.webp',
            clinicJSON.serviceLocationTypes?.length > 0 ? clinicJSON.serviceLocationTypes : [],
            clinicJSON.configurations || {},
            clinicJSON.onlineIntegration || {},
            clinicJSON?.location
        );
    }
}
